import React from 'react'
import { DndProvider, useDrop, useDrag } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { styled } from 'styled-components'
import { Student } from './Student'
import classnames from 'classnames'
import {
	useMissionStateSelector,
	useJuniorPlusStateSelector,
	getTeams,
} from '../../../context'
import {
	getStudents,
	type DisplayTeam as TeamType,
	type StudentWithPoints,
} from '@mission.io/mission-toolkit'
import { cssPlayerHeightUnit } from '../../../utility/constants'
import { rgba } from '../../../utility'
import { useSendWebsocketMessage } from '../../../context/provider'
import { useHasMissionStarted } from '../../../context/provider.jsx'

export default function TeamView({
	...props
}: {
	className?: string,
	style?: { [string]: string | number },
}): React$Node {
	const teams = useJuniorPlusStateSelector(getTeams)?.filter(
		team =>
			// do not show the flight director
			!team.isTeacherTeam &&
			// do not show teams without students
			(team.lead || team.students.length)
	)
	const studentsWithPoints = useMissionStateSelector(state =>
		getStudents(state, true)
	) // had to remove isEqual
	if (teams) {
		const count = teams.reduce(
			(total, team) => total + team.students.length + (team.lead ? 1 : 0),
			0
		)
		return (
			<DndProvider backend={HTML5Backend}>
				<Container
					{...props}
					$childCount={teams.length}
					data-walkthrough="team-view">
					{teams.map(team => (
						<Team
							key={team.displayName}
							team={team}
							classSize={count}
							studentsWithPoints={studentsWithPoints}
						/>
					))}
				</Container>
			</DndProvider>
		)
	}
	return null
}

const SMALL_CLASS_SIZE = 20

function Team({
	team,
	classSize,
	studentsWithPoints,
}: {
	team: TeamType,
	classSize: number,
	studentsWithPoints: { [string]: StudentWithPoints },
}) {
	const sendWebsocketMessage = useSendWebsocketMessage()

	const [{ isDraggingOver, canDrop }, dropRef] = useDrop({
		accept: STUDENT_DRAG_TYPE,
		canDrop(item) {
			return !team.isTeacherTeam && !studentIsOnTeam(item.id, team)
		},
		drop(item) {
			const studentId = item.id

			if (
				!studentId ||
				studentIsOnTeam(studentId, team) ||
				team.isTeacherTeam
			) {
				return
			}

			sendWebsocketMessage({
				type: 'CHANGE_STUDENT_TEAM',
				payload: {
					studentId,
					teamId: team.id,
				},
			})
		},
		collect: monitor => ({
			isDraggingOver: !!monitor.isOver(),
			canDrop: !!monitor.canDrop(),
		}),
	})

	return (
		<TeamBox ref={dropRef} $highlight={isDraggingOver && canDrop}>
			<Header>{team.displayName}</Header>
			<div
				className={classnames(
					'login-scrollbar overflow-y-auto flex-1 px-2',
					classSize > SMALL_CLASS_SIZE ? 'space-y-1' : 'space-y-2'
				)}
				style={{
					fontSize:
						classSize > SMALL_CLASS_SIZE
							? `max(2em, ${cssPlayerHeightUnit(1.75)})`
							: `max(2.5em, ${cssPlayerHeightUnit(2)})`,
				}}>
				{team.lead && (
					<StudentView
						className={classSize > SMALL_CLASS_SIZE ? '!p-1' : ''}
						student={studentsWithPoints[team.lead.id]}
					/>
				)}
				{team.students.map(student => (
					<StudentView
						className={classSize > SMALL_CLASS_SIZE ? '!p-1' : ''}
						student={studentsWithPoints[student.id]}
						key={student.id}
					/>
				))}
			</div>
		</TeamBox>
	)
}

/**
 * Checks whether the student with the given `studentId` is on the given `team`.
 */
function studentIsOnTeam(studentId: string, team: TeamType) {
	return (
		team.lead?.id === studentId || team.students.some(s => s.id === studentId)
	)
}

const getColumnCount = (childCount: number) => {
	if (childCount < 8) {
		return 4
	}
	if (childCount / 2 < 6) {
		return 6
	}

	return Math.ceil(childCount / 3)
}

const TeamBox = styled.div`
	padding: 12px 6px 6px;
	display: flex;
	flex-direction: column;
	overflow: auto;
	${({ theme, $highlight }) => `
		background: ${rgba($highlight ? theme.base : theme.base3, 0.33)};
		box-shadow: 0px 0px 6px ${theme.boxShadow};
	`}
`

const Container = styled.div`
	display: grid;
	${({ $childCount }) => {
		const totalCols = getColumnCount($childCount)
		const totalRows = Math.ceil($childCount / totalCols)

		return `
		grid-template-columns: repeat(${totalCols}, 1fr);
		grid-gap: 16px; 
		grid-template-rows: repeat(${totalRows}, auto);

		`
	}}
	${TeamBox} {
		overflow: hidden;
		min-height: 0;
	}
	overflow: hidden;
	width: 100%;
	align-items: stretch;
`

const Header = styled.div.attrs({ className: 'font-game-headline' })`
	margin: 0 0 8px;
	text-align: center;
	${({ theme }) => `
	font-weight: ${theme.fontSemiBold};
	color: ${theme.onBase};
	font-size: max(${theme.fontl}, ${cssPlayerHeightUnit(2)});`}
`

const STUDENT_DRAG_TYPE = 'student-drag-type'

/**
 * If a mission has already been started, returns a `Student` component.
 * Otherwise, returns a `DraggableStudent` component
 */
function StudentView({
	className,
	...props
}: {
	className?: string,
	student: StudentWithPoints,
}): React$Node {
	const hasMissionStarted = useHasMissionStarted()
	if (hasMissionStarted)
		return (
			<Student {...props} className={classnames(className, 'w-full p-2')} />
		)
	return (
		<DraggableStudent
			{...props}
			className={classnames(className, 'w-full p-2')}
		/>
	)
}

/**
 * A draggable version of the `Student` component. Creates the drag ref and passes it to `Student`
 */
function DraggableStudent({
	className,
	...props
}: {
	className?: string,
	student: StudentWithPoints,
}): React$Node {
	const [{ isDragging }, dragRef] = useDrag({
		type: STUDENT_DRAG_TYPE,
		item: { id: props.student.id },
		canDrag: () => !props.student.isOwner,
		collect: monitor => ({
			isDragging: monitor.isDragging(),
		}),
	})
	return (
		<Student
			{...props}
			className={classnames(
				className,
				isDragging ? 'opacity-50 cursor-grabbing' : 'cursor-grab'
			)}
			draggable={true}
			ref={dragRef}
		/>
	)
}
